import { DateRange } from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";
import { getCompleteName } from "../../../utils/misc";
import {
  AppointmentStatusName,
  getAppointmentStatusName,
} from "../AppointmentsUtils";
import {
  AppointmentsFilterValue,
  CreditCount,
} from "./AppointmentCredits.types";
import {
  CreditFragment,
  HistoryFragment,
} from "./AppointmentCredits-generated.hooks";

export function getBackText(person?: {
  firstName?: string;
  lastName?: string;
}) {
  if (!person) {
    return "Back";
  }
  if (!person.firstName && !person.lastName) {
    return "Back";
  }
  if (!person.firstName) {
    return `Back to ${person?.lastName}`;
  }
  if (!person.lastName) {
    return `Back to ${person?.firstName}`;
  }
  return `Back to ${person?.firstName} ${person?.lastName}`;
}

export function createCreditsCount(data: CreditFragment[]): CreditCount[] {
  const mapCount: { [key: string]: number } = {};
  data
    .filter((item) => item.eventStatus !== "EXPIRED")
    .forEach((item) => {
      const name = item?.appointmentType?.name;
      if (name) {
        if (!mapCount[name]) {
          mapCount[name] = 1;
        } else {
          mapCount[name]++;
        }
      }
    });
  const result = Object.entries(mapCount).map(([name, count]) => ({
    name,
    count,
  }));
  return result.sort((a, b) => a?.name?.localeCompare(b.name));
}

export interface StatusFilterOption {
  label: string;
  value: AppointmentStatusName;
}

export const statusOptions: StatusFilterOption[] = [
  { label: "Booked (unpaid)", value: "BOOKED (UNPAID)" },
  { label: "Booked", value: "BOOKED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Checked in", value: "CHECKED IN" },
  { label: "Forgiven", value: "FORGIVEN" },
  { label: "Late cancel", value: "LATE CANCEL" },
  { label: "No show", value: "NO SHOW" },
  { label: "Unpaid late cancel", value: "UNPAID LATE CANCEL" },
  { label: "Unpaid no show", value: "UNPAID NO SHOW" },
];

export function filterAppointments(
  data: HistoryFragment[],
  dateRange: DateRange,
  filter: AppointmentsFilterValue,
): HistoryFragment[] {
  return data.filter((item) => {
    // filter by date range
    const rangeStart = new Date(dateRange.start);
    const rangeEnd = new Date(dateRange.end);
    const start = new Date(item.startTime);
    if (start < rangeStart || start > rangeEnd) {
      return false;
    }

    // filter by type
    const types = filter?.types || [];
    if (
      types.length > 0 &&
      types.every((t) => t?.uuid !== item?.appointmentType?.uuid)
    ) {
      return false;
    }

    // filter by staff
    const staff = filter?.staff || [];
    if (
      staff.length > 0 &&
      staff.every((s) => s?.user_uuid !== item?.staff?.uuid)
    ) {
      return false;
    }

    // filter by status
    if (filter?.status?.length) {
      return filter.status.some((option) => {
        if (getAppointmentStatusName(item) !== option.value) {
          return false;
        }
        return true;
      });
    }

    return true;
  });
}

export const getAppointmentFilterTagsMap = (
  filter: AppointmentsFilterValue,
) => {
  const result = {};

  // tags by type
  const types = filter?.types || [];
  types.forEach((type) => {
    const key = `type-${type.uuid}`;
    const label = `Type: ${type.name}`;
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        types: filter.types.filter((item) => item.uuid !== type.uuid),
      }),
    };
  });

  // tags by staff
  const staff = filter?.staff || [];
  staff.forEach((s) => {
    const key = `staff-${s.user_uuid}`;
    const label = `Staff: ${getCompleteName(s.first_name, s.last_name)}`;
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        staff: filter.staff.filter((item) => item.user_uuid !== s.user_uuid),
      }),
    };
  });

  // tags by status
  const status = filter?.status || [];
  status.forEach((s) => {
    const key = `status-${s.label}`;
    const label = s.label;
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        status: filter?.status.filter((item) => item.label !== s.label),
      }),
    };
  });

  return result;
};
